import React, { useState, useEffect } from "react";
import {
  Button,
  Form,
  InputGroup,
  ListGroup,
  Modal,
  Spinner,
} from "react-bootstrap";
import { searchChats } from "@/entities/search/api";
import { Chat, useChatStore } from "@/entities/chat";
import { useInfiniteSearch } from "@/shared/hooks";
import { formatPhone } from "@/shared/ui";

interface SelectChatModalProps {
  show: boolean;
  onHide: () => void;
  onSelect: (chatIds: string[]) => void;
}

export const SelectChatModal: React.FC<SelectChatModalProps> = ({
  show,
  onHide,
  onSelect,
}) => {
  const chats = useChatStore(state => state.chats).filter(
    chat => chat.contact.user.status === "REGISTERED"
  );

  const {
    query: searchInput,
    setQuery: setSearchInput,
    results: searchResults,
    loading,
    containerRef,
    reset,
  } = useInfiniteSearch<Chat>({
    searchFn: searchChats,
    debounceDelay: 500,
  });

  const [selectedChatIds, setSelectedChatIds] = useState<string[]>([]);

  useEffect(() => {
    if (!show) {
      reset();
      setSelectedChatIds([]);
    }
  }, [show, reset]);

  const displayedChats = searchInput.trim()
    ? searchResults.filter(chat => chat.contact.user.status === "REGISTERED")
    : chats;

  const toggleSelectChat = (chatId: string) => {
    setSelectedChatIds(prev =>
      prev.includes(chatId)
        ? prev.filter(id => id !== chatId)
        : [...prev, chatId]
    );
  };

  const handleSend = () => {
    onSelect(selectedChatIds);
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Выберите чаты</Modal.Title>
      </Modal.Header>
      <Modal.Body>

        <InputGroup className="mb-3">
          <Form.Control
            placeholder="Поиск по имени или номеру"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="rounded-5"
          />
          {loading && (
            <InputGroup.Text>
              <Spinner size="sm" />
            </InputGroup.Text>
          )}
        </InputGroup>

        <div
          ref={containerRef}
          className="scrollbar-custom-y"
          style={{ maxHeight: 400, overflowY: "auto" }}
        >
          {displayedChats.length > 0 ? (
            <ListGroup className="gap-1">
              {displayedChats.map(chat => (
                <ListGroup.Item
                  key={chat._id}
                  action
                  active={selectedChatIds.includes(chat._id)}
                  onClick={() => toggleSelectChat(chat._id)}
                >
                  <div className="d-flex flex-column">
                    <span>{chat.contact.firstName}</span>
                    <small className="text-muted">
                      {formatPhone(chat.contact.user.phone)}
                    </small>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          ) : (
            <div className="text-center text-muted mt-3">
              {searchInput ? "Ничего не найдено" : "Нет чатов"}
            </div>
          )}

          {loading && displayedChats.length > 0 && (
            <div className="text-center mt-2">
              <Spinner animation="border" size="sm" />
            </div>
          )}
        </div>

        
        {selectedChatIds.length > 0 && (
          <div className="mt-3 d-flex justify-content-end">
            <Button variant="outline-secondary" onClick={handleSend}>Отправить ({selectedChatIds.length})</Button>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};
